import html2canvas from 'html2canvas';
import { generateHTML } from './exportHTML';
import { replaceShortcodesWithExamples } from './adAttributes';

/**
 * Convertit une image URL en base64 data URL
 * Utilise un canvas pour contourner les problèmes CORS
 * @param {string} url - L'URL de l'image
 * @returns {Promise<string>} - La data URL base64 ou l'URL originale en cas d'échec
 */
async function imageUrlToBase64(url) {
    // Si c'est déjà une data URL, la retourner telle quelle
    if (url.startsWith('data:')) {
        return url;
    }

    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Important pour CORS

        img.onload = () => {
            try {
                // Créer un canvas pour dessiner l'image
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                // Convertir en data URL
                const dataUrl = canvas.toDataURL('image/png');
                console.log(`[ThumbnailGenerator] Image converted to base64: ${url.substring(0, 50)}...`);
                resolve(dataUrl);
            } catch (error) {
                console.warn(`[ThumbnailGenerator] Canvas conversion failed for: ${url}`, error);
                resolve(url); // Fallback à l'URL originale
            }
        };

        img.onerror = () => {
            console.warn(`[ThumbnailGenerator] Failed to load image: ${url}`);
            resolve(url); // Fallback à l'URL originale
        };

        // Ajouter un timestamp pour éviter le cache
        const separator = url.includes('?') ? '&' : '?';
        img.src = `${url}${separator}_t=${Date.now()}`;
    });
}

/**
 * Extrait toutes les URLs d'images d'une chaîne HTML
 * @param {string} html - Le HTML à analyser
 * @returns {string[]} - Liste des URLs d'images
 */
function extractImageUrls(html) {
    const urls = [];
    const regex = /<img[^>]+src=["']([^"']+)["']/gi;
    let match;
    while ((match = regex.exec(html)) !== null) {
        if (!match[1].startsWith('data:')) {
            urls.push(match[1]);
        }
    }
    return [...new Set(urls)]; // Dédupliquer
}

/**
 * Génère un thumbnail (Blob) à partir d'une signature
 * @param {Array} grid - Les données de la grille de signature
 * @param {number} containerWidth - Largeur du conteneur de signature
 * @returns {Promise<Blob>} - Promise qui résout avec un Blob PNG
 */
export async function generateThumbnailBlob(grid, containerWidth = 600) {
    return new Promise(async (resolve, reject) => {
        try {
            // Générer le HTML de la signature
            let html = generateHTML(grid, containerWidth);

            // Remplacer les shortcodes AD par des valeurs d'exemple
            html = replaceShortcodesWithExamples(html);

            // Extraire et convertir toutes les URLs d'images AVANT de créer le conteneur
            const imageUrls = extractImageUrls(html);
            console.log(`[ThumbnailGenerator] Found ${imageUrls.length} unique image URLs to convert`);

            // Convertir chaque URL en base64
            for (const url of imageUrls) {
                try {
                    const base64 = await imageUrlToBase64(url);
                    if (base64.startsWith('data:')) {
                        // Remplacer toutes les occurrences de cette URL dans le HTML
                        // Échapper les caractères spéciaux pour le regex
                        const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                        html = html.replace(new RegExp(escapedUrl, 'g'), base64);
                        console.log(`[ThumbnailGenerator] Converted: ${url.substring(0, 60)}...`);
                    }
                } catch (error) {
                    console.warn(`[ThumbnailGenerator] Failed to convert: ${url}`, error);
                }
            }

            // Créer un conteneur invisible pour le rendu
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            container.style.width = `${containerWidth}px`;

            // Styles de normalisation pour un rendu cohérent
            container.style.boxSizing = 'border-box';
            container.style.margin = '0';
            container.style.padding = '0';
            container.style.lineHeight = '1';
            container.style.fontSize = '16px';
            container.style.fontFamily = 'Arial, sans-serif';

            // Le HTML contient maintenant des data URLs base64
            container.innerHTML = html;
            document.body.appendChild(container);

            // Attendre que toutes les images base64 soient chargées
            const images = container.querySelectorAll('img');
            console.log(`[ThumbnailGenerator] Waiting for ${images.length} images to load...`);

            await Promise.all(Array.from(images).map((img, index) => {
                if (img.complete && img.naturalHeight !== 0) {
                    return Promise.resolve();
                }
                return new Promise((resolve) => {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                    setTimeout(resolve, 5000);
                });
            }));

            console.log('[ThumbnailGenerator] All images loaded, capturing...');

            // Capturer avec html2canvas
            const canvas = await html2canvas(container, {
                scale: 1,
                useCORS: false,
                allowTaint: false,
                backgroundColor: '#ffffff',
                logging: false,
                imageTimeout: 15000,
                removeContainer: false
            });

            // Nettoyer le DOM
            document.body.removeChild(container);

            // Convertir le canvas en Blob
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to generate thumbnail blob'));
                }
            }, 'image/png');

        } catch (error) {
            console.error('Error generating thumbnail:', error);
            reject(error);
        }
    });
}
